import React, { useState, useEffect, useRef, useCallback } from 'react';
import './NotificationPanel.scss';
import { NotificationEvent } from '../../../types/Notificationtypes';
import { fetchNotifications } from '../../../services/notificationService';
import { ReactComponent as AlarmIcon } from '../../../images/alert-circle.svg';
import { ReactComponent as EventIcon } from '../../../images/check-circle.svg';
import { ReactComponent as NeutralIcon } from '../../../images/info-circle-grey.svg';
import { Link } from 'react-router-dom';
import { convertTimeFromSpecificZoneToLocalAndTimeAgo } from '../TimeConvertorHelper';
import { USER_ERROR_CONDITIONS } from '../NotificationConstants';

const NotificationPanel = () => {
  const [tab, setTab] = useState<'global' | 'user'>('global');
  const [notifications, setNotifications] = useState<NotificationEvent[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loader = useRef<IntersectionObserver>();
  const lastFetchTab = useRef<'user' | 'global'>(tab);
  const groupName = "All Wells"



  // --- Helper: Determine severity class (used for color coding) ---
  const getSeverityClass = (
    note: string | null | undefined,
    type: string | null | undefined,
    status: string | null | undefined,
    date: string | null | undefined,
  ) => {
    if (tab === 'user') {
      if (!status || !date) return 'error';

      if (status.toLowerCase().includes('ok')) {
        return 'ok';
      }

      for (const condition of USER_ERROR_CONDITIONS) {
        if (status.toLowerCase().includes(condition)) {
          return 'error';
        }
      }
      return 'neutral';
    } else {
      if (!type || !note) return 'neutral';

      const lowerNote = note.toLowerCase();
      const lowerType = type.toLowerCase();

      if (lowerNote.includes('failure') || lowerNote.includes('shutdown') || lowerType === 'alarm') {
        return 'error';
      }
      if (lowerNote.includes('cleared') || lowerNote.includes('started') || lowerType === 'status change') {
        return 'ok';
      }
    }
    return 'neutral';
  };

  const getTypeClass = (type: string | null | undefined) => {
    if (!type) return 'unknown';
    return type.toLowerCase().replace(/\s/g, '');
  };

  // --- Render proper icon based on severity ---
  const renderIcon = (
    type: string | null | undefined,
    note: string | null | undefined,
    status: string | null | undefined,
    date: string | null | undefined,
  ) => {
    const severity = getSeverityClass(note, type, status, date);

    if (severity === 'error') return <AlarmIcon className='notification-icon' />;
    if (severity === 'ok') return <EventIcon className='notification-icon' />;
    return <NeutralIcon className='notification-icon' />;
  };

  // --- Main API Call: Load notification data (with pagination and tab mode) ---
  const loadNotifications = useCallback(async (currentTab: 'user' | 'global', currentPage: number) => {
    lastFetchTab.current = currentTab;
    try {
      const res = await fetchNotifications(currentPage, currentTab === 'user');
      // Ignore response if tab switched meanwhile
      if (lastFetchTab.current !== currentTab) return;

      if (res.values.length === 0 && currentPage === 1) {
        setNotifications([]);
        setHasMore(false);
        return;
      }

      if (currentPage > 1) {
        // Appending additional results for pagination
        setNotifications((prev) => [...prev, ...res.values]);
      } else {
        // First page results (or tab switch)
        setNotifications(res.values);
      }

      // Determines if there's potentially more data to load
      setHasMore(res.values.length === 50);
    } catch (e) {
      console.error('Error loading notifications', e);
    }
  }, []);

  // --- Load data on tab or page change ---
  useEffect(() => {
    loadNotifications(tab, page);
  }, [tab, page, loadNotifications]);

  const handleTabSwitch = (newTab: 'user' | 'global') => {
    setNotifications([]);
    setHasMore(true);
    setPage(1);
    setTab(newTab);
  };

  // --- Infinite scroll: Detect last item in viewport to auto-fetch more ---
  const lastNotificationRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!hasMore) return;
      if (loader.current) loader.current.disconnect();
      loader.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) loader.current.observe(node);
    },
    [hasMore],
  );

  return (
    <div className='notification-panel'>
      <div className='notification-header'>Notifications</div>

      <div className='notification-tabs'>
        <div className='tab-wrapper'>
          <button
            className={`tab-btn ${tab === 'user' ? 'active user-tab' : ''}`}
            onClick={() => handleTabSwitch('user')}
          >
            User
          </button>
          <button
            className={`tab-btn ${tab === 'global' ? 'active global-tab' : ''}`}
            onClick={() => handleTabSwitch('global')}
          >
            Global
          </button>
        </div>
      </div>

      <div className='notification-body'>
        {notifications.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Loading notifications...</div>
        ) : (
          notifications.map((n, i) => {
            const typeClass = getTypeClass(n.eventTypeName);
            const severityClass = getSeverityClass(n.note, n.eventTypeName, n.status, n.date);

            return (
              <div
                key={n.eventId}
                className={`notification-item ${typeClass} ${severityClass}`}
                ref={i === notifications.length - 1 ? lastNotificationRef : null}
              >
                <div className='top-row'>
                  <div className='badge-wrapper'>
                    {renderIcon(n.eventTypeName, n.note, n.status, n.date)}
                    <div className={`badge ${severityClass}`}>{n.eventTypeName || ' --------- '}</div>
                  </div>
                  <div className='time'>{convertTimeFromSpecificZoneToLocalAndTimeAgo(n.date, n.wellTimezone)}</div>
                </div>
                <div className='desc'>{n.note || '-'}</div>
                <div className='well-name'>
                  <Link
                    to={`/asset/status/${n.assetId}/${groupName}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >{n.nodeId}</Link>
                </div>
              </div>
            );
          })
        )}

        {!hasMore && notifications.length > 0 && <div className='end-indicator'>No more notifications</div>}
      </div>
    </div>
  );
};

export default NotificationPanel;
